import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  DoubtsAiService,
  usePostDc1V2CreateDoubt,
  usePostDc1V2ImageOCR,
} from "@oneapp/data";
import { AnalyticsEvents, SqsEvents, useAnalytics } from "@repo/analytics";
import { Box, Button, Modal, Text } from "@repo/ui";
import type { DoubtSession } from "@repo/doubts-web";
import {
  AdjustBoxModal,
  type GetAnswersChatSession,
  InappropriateImageModal,
  type ImageUploadCompletePayload,
  CREATE_DOUBT_SOURCE_AINA,
  isCreateDoubtQuotaExhaustedError,
  programIdForCreateDoubtBody,
  readCreateDoubtDoubtId,
  readImageOcrInappropriateFlag,
  readImageOcrUnreadableFlag,
  readImageOcrV2ImageFields,
  getDoubtRequestPlatform,
  mapSubjectNameToApiSubject,
  saveAinaChatBootstrap,
} from "@repo/doubts-web";
import { ROUTESWEB } from "@repo/utils";

import { setAinaReturnAgentForDoubtsChat } from "../lib/aina/doubtsReturnFromAina";

export type AinaCameraDoubtFlowProps = {
  open: boolean;
  onClose: () => void;
  doubtSession: DoubtSession;
  agent: string;
};

/**
 * AINA / LiveKit (`lk-room-*`) uses full-viewport overlays around z-index 9_999_999_999.
 * `@repo/ui` Modal portals to `document.body` with default ~2k, so it stacks under that layer
 * and receives no clicks. Stay strictly above the room overlay.
 */
const AINA_STACKING_OVERLAY_Z = 10_000_000_000;

/**
 * In-AINA flow (parity with doubts AskQuestions image path):
 * camera → native file picker → AdjustBoxModal (crop + subject) → OCR + create doubt → navigate to doubts chat.
 * Does not navigate to /doubts until the doubt is created.
 */
export function AinaCameraDoubtFlow({
  open,
  onClose,
  doubtSession,
  agent,
}: AinaCameraDoubtFlowProps) {
  const { t } = useTranslation("doubts");
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const awaitingPickerRef = useRef(false);
  /** Ignore next N window focus events (modal close / crop cancel fire focus before file dialog). */
  const skipPickerFocusClosesRef = useRef(0);
  const cropOpenRef = useRef(false);
  const cropImageUrlRef = useRef<string | null>(null);

  const [cropOpen, setCropOpen] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const [flowError, setFlowError] = useState<string | null>(null);
  const [imageIssueModal, setImageIssueModal] = useState<
    "inappropriate" | "unreadable" | null
  >(null);

  cropOpenRef.current = cropOpen;
  cropImageUrlRef.current = cropImageUrl;

  const { mutateAsync: createDoubtAsync } = usePostDc1V2CreateDoubt({
    request: { clientName: "doubtsDCAPI" },
  });

  const { mutateAsync: runImageOcrAsync } = usePostDc1V2ImageOCR({
    request: { clientName: "doubtsDCAPI" },
  });

  const subjectsQuery = DoubtsAiService.useGetDc1MetaSubjectsGradeMapping(
    {
      gradeId: doubtSession.gradeId!,
      tenantId: doubtSession.tenantId!,
    },
    {
      request: { clientName: "doubtsAi" },
      query: {
        enabled:
          Boolean(doubtSession.gradeId) &&
          Boolean(doubtSession.tenantId) &&
          open,
      },
    },
  );

  const apiSubjects = useMemo(() => {
    const data = subjectsQuery.data as unknown;
    if (!Array.isArray(data)) return [];
    return data
      .map((item: unknown) => {
        const row = item as {
          subjectId?: number;
          subject?: { name?: string };
        };
        return {
          subjectId: row.subjectId as number,
          name: row.subject?.name ?? "",
        };
      })
      .filter(
        (s) =>
          s.subjectId != null &&
          Number.isFinite(s.subjectId) &&
          s.name.length > 0,
      );
  }, [subjectsQuery.data]);

  const revokeCropUrl = useCallback(() => {
    setCropImageUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const triggerFilePicker = useCallback(() => {
    awaitingPickerRef.current = true;
    queueMicrotask(() => fileInputRef.current?.click());
  }, []);

  /** Closing the error modal often emits multiple focus events before the file dialog opens. */
  const retryAfterError = useCallback(() => {
    skipPickerFocusClosesRef.current = 2;
    setFlowError(null);
    queueMicrotask(() => triggerFilePicker());
  }, [triggerFilePicker]);

  const reuploadAfterImageIssue = useCallback(() => {
    skipPickerFocusClosesRef.current = 2;
    setImageIssueModal(null);
    queueMicrotask(() => triggerFilePicker());
  }, [triggerFilePicker]);

  const resetLocal = useCallback(() => {
    awaitingPickerRef.current = false;
    skipPickerFocusClosesRef.current = 0;
    revokeCropUrl();
    setCropOpen(false);
    setFlowError(null);
    setImageIssueModal(null);
  }, [revokeCropUrl]);

  useEffect(() => {
    if (!open) {
      resetLocal();
      return;
    }
    setFlowError(null);
    triggerFilePicker();
  }, [open, resetLocal, triggerFilePicker]);

  /** If the user dismisses the file dialog without choosing a file, close the flow (no intermediate modal). */
  useEffect(() => {
    if (!open) return;

    const onFocus = () => {
      window.setTimeout(() => {
        if (skipPickerFocusClosesRef.current > 0) {
          skipPickerFocusClosesRef.current -= 1;
          return;
        }
        if (!awaitingPickerRef.current) return;
        if (cropOpenRef.current) return;
        if (cropImageUrlRef.current) return;
        awaitingPickerRef.current = false;
        onClose();
      }, 400);
    };

    window.addEventListener("focus", onFocus, true);
    return () => window.removeEventListener("focus", onFocus, true);
  }, [open, onClose]);

  const openCropWithFile = useCallback(
    (file: File) => {
      awaitingPickerRef.current = false;
      revokeCropUrl();
      const url = URL.createObjectURL(file);
      setCropImageUrl(url);
      setCropOpen(true);
    },
    [revokeCropUrl],
  );

  /** User cancelled crop/subject — leave the doubts camera flow and stay on AINA (no second file picker). */
  const cancelCropFlow = useCallback(() => {
    resetLocal();
    onClose();
  }, [resetLocal, onClose]);

  const runImageDoubtFlow = useCallback(
    async (payload: ImageUploadCompletePayload) => {
      if (
        !doubtSession.tenantId ||
        doubtSession.gradeId == null ||
        doubtSession.uamId == null ||
        programIdForCreateDoubtBody(doubtSession.programId) == null
      ) {
        setFlowError(t("session_missing_subjects"));
        URL.revokeObjectURL(payload.previewObjectUrl);
        return;
      }
      setFlowError(null);
      try {
        const ocrRes = await runImageOcrAsync({
          data: { file: payload.blob },
        });
        const issue: "inappropriate" | "unreadable" | null =
          readImageOcrInappropriateFlag(ocrRes)
            ? "inappropriate"
            : readImageOcrUnreadableFlag(ocrRes)
              ? "unreadable"
              : null;
        if (issue != null) {
          setImageIssueModal(issue);
          setFlowError(null);
          URL.revokeObjectURL(payload.previewObjectUrl);
          return;
        }
        const qRaw = (ocrRes as { data?: { question?: string } })?.data
          ?.question;
        const q = typeof qRaw === "string" ? qRaw.trim() : "";
        const doubtText = q || t("ocr_no_text");
        const { imageId, imagePath } = readImageOcrV2ImageFields(ocrRes);

        const createBody: Record<string, unknown> = {
          doubtText,
          freeDoubt: true,
          subjectId: payload.subjectId,
          tenantId: doubtSession.tenantId,
          userGradeId: doubtSession.gradeId,
          userId: doubtSession.uamId,
          platform: getDoubtRequestPlatform(),
          source: CREATE_DOUBT_SOURCE_AINA,
          programId: programIdForCreateDoubtBody(doubtSession.programId),
        };
        if (imageId != null) {
          createBody.imageId = imageId;
        }
        if (imagePath != null) {
          createBody.imagePath = imagePath;
          createBody.imageUrl = imagePath;
        }

        const createRes = await createDoubtAsync({ data: createBody });
        const doubtId = readCreateDoubtDoubtId(createRes);
        if (createRes.success && createRes.doubtId) {
          SqsEvents.actDoubtActivity({
            subject: payload.subjectName,
          });
        }
        track(AnalyticsEvents.DOUBTS_QUESTION_CREATED, 1, {
          submission_type: "image",
        });

        const chatPayload: GetAnswersChatSession = {
          kind: "image",
          fromAinaHost: true,
          doubtId,
          subjectId: payload.subjectId,
          questionText: doubtText,
          subjectDisplayName: payload.subjectName,
          subjectForSolution: mapSubjectNameToApiSubject(payload.subjectName),
          imageUrl: payload.previewObjectUrl,
          ...(doubtSession.qbId != null && doubtSession.qbId > 0
            ? { qbId: doubtSession.qbId }
            : {}),
        };

        setAinaReturnAgentForDoubtsChat(agent);
        saveAinaChatBootstrap(chatPayload);
        navigate(`/${ROUTESWEB.APP.DOUBTS}/aina-chat`, {
          state: { ainaChatBootstrap: chatPayload },
        });
        resetLocal();
        onClose();
      } catch (e) {
        if (readImageOcrInappropriateFlag(e)) {
          setImageIssueModal("inappropriate");
          setFlowError(null);
        } else if (isCreateDoubtQuotaExhaustedError(e)) {
          resetLocal();
          onClose();
        } else {
          const extra =
            e instanceof Error && /^doubt_dc_\d+$/.test(e.message)
              ? ` (${e.message.replace("doubt_dc_", "")})`
              : "";
          setFlowError(t("doubt_create_failed") + extra);
        }
        URL.revokeObjectURL(payload.previewObjectUrl);
      }
    },
    [
      agent,
      createDoubtAsync,
      doubtSession,
      navigate,
      onClose,
      resetLocal,
      runImageOcrAsync,
      t,
      track,
    ],
  );

  const handleBrowseImageComplete = useCallback(
    async (payload: ImageUploadCompletePayload) => {
      await runImageDoubtFlow(payload);
      revokeCropUrl();
      setCropOpen(false);
    },
    [revokeCropUrl, runImageDoubtFlow],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      e.target.value = "";
      if (f?.type.startsWith("image/")) {
        openCropWithFile(f);
      }
    },
    [openCropWithFile],
  );

  if (!open) {
    return null;
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />

      {flowError ? (
        <Modal
          isModalOpen
          onClose={() => {
            resetLocal();
            onClose();
          }}
          showFooter={false}
          width="min(27rem, 92vw)"
          height="auto"
          headerConfig={{ title: t("doubt_create_failed") }}
          body={
            <Box style={{ padding: "1rem", maxWidth: "100%" }}>
              <Text style={{ marginBottom: 16 }}>{flowError}</Text>
              <Box
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "flex-end",
                }}
              >
                <Button variant="primary" onPress={retryAfterError}>
                  {t("retry")}
                </Button>
              </Box>
            </Box>
          }
          styles={{
            overlay: { zIndex: AINA_STACKING_OVERLAY_Z },
            modal: { zIndex: AINA_STACKING_OVERLAY_Z + 1 },
          }}
        />
      ) : null}

      <InappropriateImageModal
        isOpen={imageIssueModal != null}
        variant={imageIssueModal ?? "inappropriate"}
        onReupload={reuploadAfterImageIssue}
        overlayZIndex={AINA_STACKING_OVERLAY_Z}
      />

      {cropOpen && cropImageUrl ? (
        <AdjustBoxModal
          isOpen={cropOpen}
          imageUrl={cropImageUrl}
          onCancel={cancelCropFlow}
          onComplete={(p) => handleBrowseImageComplete(p)}
          subjects={apiSubjects}
          subjectsLoading={subjectsQuery.isLoading}
          subjectsError={subjectsQuery.isError}
          sessionSubjectsReady={Boolean(
            doubtSession.gradeId && doubtSession.tenantId,
          )}
          onRetrySubjects={() => void subjectsQuery.refetch()}
          overlayZIndex={AINA_STACKING_OVERLAY_Z}
        />
      ) : null}
    </>
  );
}
